import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Warning as WarningIcon,
  CheckBoxOutlineBlank as CheckBoxIcon 
} from '@mui/icons-material';

/**
 * IncompleteDialog Component
 * 
 * Displays when a user attempts to submit an incomplete survey.
 * Provides information about what needs to be completed and guides the user
 * back to the survey to finish it.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback function when dialog is closed
 * @param {Array} props.missingItems - Optional array of specific missing items
 */
const IncompleteDialog = ({ 
  open, 
  onClose, 
  missingItems = [] 
}) => {
  /**
   * Default requirements if no specific missing fields are provided
   */
  const defaultRequirements = [
    'All survey questions must be answered',
    'Explanations must be provided for all failing items',
    'Responsibility must be assigned for all issues',
    'Strengths summary must be completed',
    'Opportunities summary must be completed'
  ];

  const requirements = missingItems.length > 0 ? missingItems : defaultRequirements;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="incomplete-dialog-title"
      aria-describedby="incomplete-dialog-description"
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          height: '150vh',
        },
      }}
    >
      <DialogTitle id="incomplete-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon color="warning" sx={{ fontSize: 32 }} />
          <Typography variant="h6" component="span" style={{ fontFamily: 'Lato' }}>
            Survey Incomplete
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText id="incomplete-dialog-description">
          <Typography variant="body1" paragraph style={{ fontFamily: 'Lato' }}>
            Your survey cannot be submitted because some required information is missing. 
            Please complete all required fields before submitting.
          </Typography>
        </DialogContentText>

        {/* Requirements Checklist */}
        <Box sx={{ mt: 2 }}>
          <Typography 
            variant="subtitle2" 
            style={{ 
              fontFamily: 'Lato',
              fontWeight: 'bold',
              marginBottom: 12
            }}
          >
            {missingItems.length > 0 
              ? 'The following items need to be completed:' 
              : 'Please ensure the following requirements are met:'
            }
          </Typography>
          
          <List dense>
            {requirements.map((requirement, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckBoxIcon color="action" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="body2" style={{ fontFamily: 'Lato' }}>
                      {requirement}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Helpful Tips */}
        <Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            backgroundColor: 'info.50',
            border: 1,
            borderColor: 'info.main',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            Helpful Tips:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Use the tabs at the top to navigate between survey sections<br />
            • Look for red error indicators on fields that need attention<br />
            • Questions marked as "No" require additional explanation and responsibility assignment<br />
            • Your progress is automatically saved as you work
          </Typography>
        </Box>

        {/* Auto-save Notice */}
        <Typography 
          variant="body2" 
          color="textSecondary" 
          style={{ 
            fontFamily: 'Lato',
            fontStyle: 'italic',
            marginTop: 16
          }}
        >
          <strong>Note:</strong> Your current progress has been automatically saved. 
          You can safely continue working on the survey without losing any data.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          size="large"
          fullWidth
        >
          Continue Survey
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncompleteDialog;